import { CommandBarButton } from "@fluentui/react";
import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import { SettingButtonsDisable } from "../types/types";
import { ColumnOptionsContext } from "./ColumnOptions";
import { ViewDialogState } from "./ColumnOptionsPanel";
import { isColumnFiltered } from "./utils";

type Props = {
  setViewDialog: (viewDialogState: ViewDialogState) => void;
  disableButtons?: SettingButtonsDisable;
};

export const ViewToolbar = ({ setViewDialog, disableButtons }: Props) => {
  const {
    selectedView,
    onClickDeleteView,
    onClickClearAllFilters,
    onClickReset,
    onClickSaveView,
    columns,
    strings,
    hideViewOptionsFeatures,
    disableNewView,
    hasUnsavedChanges,
    id,
  } = useContext(ColumnOptionsContext);

  const areAnyFiltersSelected = useMemo(
    () => columns.some((c) => isColumnFiltered(c.filter)),
    [columns]
  );

  return (
    <Bar>
      {!hideViewOptionsFeatures && (
        <CommandBarButton
          iconProps={{ iconName: "Add" }}
          text={strings.newViewButtonLabel}
          disabled={
            (typeof disableNewView === "undefined" ? false : disableNewView) ||
            disableButtons?.new
          }
          onClick={() =>
            setViewDialog({ isOpen: true, userAppSettingId: null })
          }
          id={id ? `${id}-btn-new` : undefined}
        />
      )}
      {!hideViewOptionsFeatures && (
        <CommandBarButton
          iconProps={{ iconName: "Save" }}
          text={strings.saveViewButtonLabel}
          disabled={disableButtons?.save || !selectedView}
          onClick={onClickSaveView}
          className={hasUnsavedChanges ? "save-dirty" : undefined}
          id={id ? `${id}-btn-save-view` : undefined}
        />
      )}
      {!hideViewOptionsFeatures && (
        <CommandBarButton
          iconProps={{ iconName: "Edit" }}
          text={strings.editViewButtonLabel}
          disabled={disableButtons?.edit || !selectedView}
          onClick={() => {
            if (!selectedView) return;
            setViewDialog({
              isOpen: true,
              userAppSettingId: selectedView.userAppSettingId,
            });
          }}
          id={id ? `${id}-btn-edit` : undefined}
        />
      )}
      <CommandBarButton
        iconProps={{ iconName: "Undo" }}
        text={strings.resetButtonLabel}
        disabled={disableButtons?.reset}
        onClick={onClickReset}
        id={id ? `${id}-btn-reset` : undefined}
      />
      <CommandBarButton
        iconProps={{ iconName: "ClearFilter" }}
        text={strings.clearFiltersButtonLabel}
        disabled={disableButtons?.clearAllFilters || !areAnyFiltersSelected}
        onClick={onClickClearAllFilters}
        id={id ? `${id}-btn-clear` : undefined}
      />
      {!hideViewOptionsFeatures && (
        <CommandBarButton
          iconProps={{ iconName: "Delete" }}
          text={strings.deleteViewButtonLabel}
          disabled={disableButtons?.delete || !selectedView}
          onClick={onClickDeleteView}
          id={id ? `${id}-btn-delete` : undefined}
        />
      )}
    </Bar>
  );
};

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  .ms-Button {
    height: 36px;
    background: transparent;
  }
  .save-dirty {
    background: ${(p) => p.theme.palette.themeLighterAlt};
  }
  .save-dirty:not([disabled]) .ms-Button-icon,
  .save-dirty:not([disabled]) .ms-Button-label {
    color: ${(p) => p.theme.palette.themePrimary};
    font-weight: 700;
  }
`;
